import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { getTicketsByParkingLocation } from 'api/canpark/tickets/getTicketsByParkingLocation';
import { Ticket } from '../types/ticket';

export interface LotHistoryDisplayFilter {
	ticketNumber: string;
	licensePlate: string;
	showLog: boolean;
	showLogAndWarn: boolean;
	showTow: boolean;
	startDate?: string;
	endDate?: string;
}

export interface UseLotHistoryInfiniteResult {
	tickets: Ticket[];
	isLoadingInitial: boolean;
	isLoadingMore: boolean;
	hasMore: boolean;
	error: unknown;
	loadMore: () => void;
	reset: () => void;
}

export const LOT_HISTORY_PAGE_SIZE = 20;

const sortByDateDesc = (list: Ticket[]) =>
	[...list].sort(
		(a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
	);

export const useLotHistoryInfinite = (
	parkingLocationId: string | null | undefined,
	filter: LotHistoryDisplayFilter,
): UseLotHistoryInfiniteResult => {
	const [tickets, setTickets] = useState<Ticket[]>([]);
	const [isLoadingInitial, setIsLoadingInitial] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [hasMore, setHasMore] = useState(false);
	const [error, setError] = useState<unknown>(null);

	const reqIdRef = useRef(0);
	const nextPageRef = useRef(1);
	const inFlightRef = useRef(false);

	const fetchPage = useCallback(
		async (
			id: string,
			f: LotHistoryDisplayFilter,
			page: number,
			mode: 'initial' | 'more',
		) => {
			if (inFlightRef.current) return;
			inFlightRef.current = true;
			const reqId = ++reqIdRef.current;

			if (mode === 'initial') setIsLoadingInitial(true);
			else setIsLoadingMore(true);
			setError(null);

			try {
				const result = await getTicketsByParkingLocation(id, {
					ticketNumber: f.ticketNumber,
					licensePlate: f.licensePlate,
					showLog: f.showLog,
					showLogAndWarn: f.showLogAndWarn,
					showTow: f.showTow,
					startDate: f.startDate,
					endDate: f.endDate,
					items_per_page: LOT_HISTORY_PAGE_SIZE + 1,
					page,
				});

				if (reqId !== reqIdRef.current) return;

				const more = result.length > LOT_HISTORY_PAGE_SIZE;
				const slice = more
					? result.slice(0, LOT_HISTORY_PAGE_SIZE)
					: result;

				setHasMore(more);
				nextPageRef.current = page + 1;
				setTickets((prev) =>
					sortByDateDesc(mode === 'initial' ? slice : [...prev, ...slice]),
				);
			} catch (e) {
				if (reqId !== reqIdRef.current) return;
				setError(e);
				if (mode === 'initial') setTickets([]);
				setHasMore(false);
			} finally {
				if (reqId === reqIdRef.current) {
					if (mode === 'initial') setIsLoadingInitial(false);
					else setIsLoadingMore(false);
				}
				inFlightRef.current = false;
			}
		},
		[],
	);

	const startInitial = useCallback(
		(id: string, f: LotHistoryDisplayFilter) => {
			// New filter context: invalidate any in-flight request and reset paging.
			++reqIdRef.current;
			inFlightRef.current = false;
			nextPageRef.current = 1;
			setTickets([]);
			setHasMore(false);
			fetchPage(id, f, 1, 'initial');
		},
		[fetchPage],
	);

	const debouncedStartInitial = useRef(
		debounce(
			(id: string, f: LotHistoryDisplayFilter) => startInitial(id, f),
			500,
		),
	).current;

	useEffect(
		() => () => {
			debouncedStartInitial.cancel();
		},
		[debouncedStartInitial],
	);

	useEffect(() => {
		if (!parkingLocationId) {
			++reqIdRef.current;
			inFlightRef.current = false;
			nextPageRef.current = 1;
			setTickets([]);
			setHasMore(false);
			setIsLoadingInitial(false);
			setIsLoadingMore(false);
			return;
		}
		debouncedStartInitial(parkingLocationId, filter);
	}, [
		parkingLocationId,
		filter.ticketNumber,
		filter.licensePlate,
		filter.showLog,
		filter.showLogAndWarn,
		filter.showTow,
		filter.startDate,
		filter.endDate,
		debouncedStartInitial,
		filter,
	]);

	const loadMore = useCallback(() => {
		if (!parkingLocationId) return;
		if (inFlightRef.current) return;
		if (!hasMore) return;
		fetchPage(parkingLocationId, filter, nextPageRef.current, 'more');
	}, [parkingLocationId, filter, hasMore, fetchPage]);

	const reset = useCallback(() => {
		if (!parkingLocationId) return;
		debouncedStartInitial.cancel();
		startInitial(parkingLocationId, filter);
	}, [parkingLocationId, filter, debouncedStartInitial, startInitial]);

	return {
		tickets,
		isLoadingInitial,
		isLoadingMore,
		hasMore,
		error,
		loadMore,
		reset,
	};
};
